import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/8135492/pexels-photo-8135492.jpeg";

const lines = ["Generational Wealth.", "Tangible Assets."];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onExplore }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
        <img src={HERO_IMG} alt="Modern luxury living space" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-transparent to-[#0B0C10]/60" />
      </motion.div>

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 h-full flex flex-col justify-end px-6 md:px-12 pb-24 md:pb-28 max-w-[1600px] mx-auto">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="font-mono-lux text-[11px] md:text-xs uppercase tracking-[0.35em] text-[#C8A97E] mb-6"
          data-testid="hero-overline"
        >
          Keyscape Advisory — Estates &amp; Capital
        </motion.p>

        <h1 className="font-display font-medium text-[#F8F9FA] text-[13vw] sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[1.02] tracking-tight" data-testid="hero-heading">
          {lines.map((line, i) => (
            <MaskedLine key={line} delay={0.55 + i * 0.18}>
              {i === 1 ? <span className="italic text-[#C8A97E]">{line}</span> : line}
            </MaskedLine>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2 }}
          className="mt-6 max-w-xl text-sm md:text-base text-[#B9BCC4] leading-relaxed"
        >
          Land. Luxury residences. Market-linked growth. Ironclad protection —
          one private desk for everything you own, and everything you will.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.4 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-book-consultation-button"
            className="btn-gold group flex items-center gap-3 px-8 py-4 text-[11px] font-mono-lux uppercase tracking-[0.2em] font-medium"
          >
            <span>Book a Consultation</span>
            <ArrowRight size={15} className="relative z-10 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <button
            onClick={onExplore}
            data-testid="hero-explore-button"
            className="btn-ghost-lux group flex items-center gap-3 px-8 py-4 text-[11px] font-mono-lux uppercase tracking-[0.2em]"
          >
            Explore the Portfolio
            <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-1" />
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 right-6 md:right-12 z-10 hidden md:flex items-center gap-3 font-mono-lux text-[10px] uppercase tracking-[0.3em] text-[#8A8D98]"
      >
        <span className="w-10 h-px bg-[#8A8D98]/50" />
        Scroll
      </motion.div>
    </section>
  );
};
